<template>
  <div class="flex flex-col gap-2">
    <div v-if="itemTypeAvailable.length > 1 && displayPackTab">
      <WalkmanButton
        v-model="itemsType"
        :options="filteredItemTypeAvailable"
        size="small"
        w-full
      />
    </div>
    <!--#region header -->
    <div v-if="['item'].includes(itemsType)" class="flex flex-col gap-2">
      <SelectItemCategory
        v-if="canEditCategory"
        v-model="localSelectedCategory"
        :edition-mode="true"
        :funeral-home-id="funeralHomeId"
      />
      <h1 v-else class="text-xl font-bold">
        {{ categoryName }}
      </h1>
      <div
        v-if="Array.isArray(children_categories) && children_categories.length"
      >
        <SelectComponent
          v-model="selected_children_category"
          :values="children_categories"
          track-by="id"
          display-key="name_index"
          name="children_categories"
        />
      </div>
    </div>
    <div v-else>
      <h1 class="capitalize-first text-lg font-bold">
        {{
          filteredItemTypeAvailable.find((type) => type.value === itemsType)
            .label
        }}
      </h1>
    </div>
  </div>
  <!--#endregion -->
  <div
    v-if="globalSearch"
    class="flex flex-shrink-0 flex-row items-center gap-2"
  >
    <div class="flex flex-1">
      <BaseSearchInput
        v-model="searchText"
        :placeholder="$t('global.search_for_an_item', 'Search for an item')"
      />
    </div>
  </div>
  <div v-else class="flex flex-shrink-0 flex-row items-center gap-2">
    <div class="flex flex-1">
      <BaseSearchInput v-model="filterText" />
    </div>
    <div
      v-if="['dossier', 'item'].includes(itemsType)"
      class="flex flex-shrink-0 flex-row items-center gap-2"
    >
      <button
        class="h-9 w-9 rounded border bg-white"
        @click="favorite = !favorite"
      >
        <FontAwesomeIcon
          :icon="['fas', 'star']"
          :class="favorite ? 'text-yellow-300' : 'text-gray-700'"
        />
      </button>
      <button
        class="h-9 w-9 rounded border bg-white"
        @click="best_selling = !best_selling"
      >
        <FontAwesomeIcon
          :icon="['fas', 'chart-line-up']"
          :class="best_selling ? 'text-indigo-600' : 'text-gray-700'"
        />
      </button>
    </div>
  </div>
  <div v-if="suppliers?.length > 0" class="-mt-2">
    <SelectComponent
      v-model="selected_supplier"
      :values="suppliers"
      track-by="id"
      :label="$t('attributes.supplier')"
      display-key="name"
      name="supplier"
    />
  </div>

  <div
    ref="scrollContainer"
    class="-mr-2.5 flex flex-1 flex-col overflow-y-auto pr-2.5"
    @scroll="debouncedHandleScroll"
  >
    <div v-if="loading" class="flex flex-1 flex-col justify-center">
      <LoadingComponent :display-label="false" />
    </div>
    <div
      v-else-if="globalSearch && items === null"
      class="flex flex-1 flex-col items-center justify-center"
    >
      <span
        class="mt-4 w-3/4 rounded border bg-white px-1 py-0.5 text-center text-xl font-bold"
      >
        {{ $t('global.taper_qq') }}
      </span>
    </div>
    <div
      v-else-if="!globalSearch && items === null"
      class="flex flex-1 flex-col items-center justify-center"
    >
      <span
        class="w-3/4 rounded border bg-white px-1 py-0.5 text-center text-xl font-bold"
      >
        {{
          $t('global.there_is_no_yet', {
            settings: $t(`enums.invoicing.defaultItemCategories.${category}`)
          })
        }}
      </span>
    </div>
    <div
      v-else-if="Array.isArray(items) && items.length === 0"
      class="flex flex-1 flex-col items-center justify-center"
    >
      <span
        class="w-3/4 rounded border bg-white px-1 py-0.5 text-center text-xl font-bold"
      >
        {{ $t('global.is_empty_list') }}
      </span>
    </div>
    <div v-else-if="filteredItem" class="flex flex-1 flex-col gap-2">
      <template v-if="itemsType === 'pack'">
        <PackModalCard
          v-for="pack in filteredItem"
          :key="pack.id"
          :pack="pack"
          :disable-buttons="disableButtons"
          @selection="selectedItem"
        />
      </template>
      <template v-else-if="itemsType === 'automation'">
        <ItemModalCard
          v-for="item in items"
          :key="item.id"
          :item="item"
          :selected="
            itemsType === 'dossier'
              ? alreadyAddedItemsDossier.includes(item.item_id)
              : alreadyAddedItems.includes(item.id)
          "
          :hide-price="hidePrice"
          :hide-quantity-input="hideQuantityInput"
          :hide-stock-disponibility="hideStockDisponibility"
          :currency="defaultCurrency"
          @selection="selectedItem"
        />
      </template>
      <template v-else>
        <Transition name="fade" mode="out-in">
          <div v-if="variants.length === 0" class="flex flex-col gap-2">
            <ItemModalCard
              v-for="item in filteredItem"
              :key="item.id"
              :item="item"
              :linked-components="componentsByParentId[item.id] ?? []"
              :currency="defaultCurrency"
              :selected="
                itemsType === 'dossier'
                  ? alreadyAddedItemsDossier.includes(item.item_id)
                  : alreadyAddedItems.includes(item.id)
              "
              :hide-price="hidePrice"
              :hide-quantity-input="hideQuantityInput"
              :hide-stock-disponibility="hideStockDisponibility"
              :show-category="item.item_category_id !== localSelectedCategory"
              @show-variants="
                (selectedVariants) => (variants = selectedVariants)
              "
              @selection="selectedItem"
            />
          </div>
          <div v-else class="flex flex-col gap-2">
            <div class="flex flex-row justify-end">
              <button
                class="rounded bg-gradient-to-b from-red-600 to-red-700 px-2 py-0.5 text-white"
                @click="() => (variants = [])"
              >
                <FontAwesomeIcon :icon="['fal', 'times']" />
              </button>
            </div>
            <ItemModalCard
              v-for="item in variants"
              :key="item.id"
              :item="item"
              :currency="defaultCurrency"
              :selected="
                itemsType === 'dossier'
                  ? alreadyAddedItemsDossier.includes(item.item_id)
                  : alreadyAddedItems.includes(item.id)
              "
              :hide-price="hidePrice"
              :hide-quantity-input="hideQuantityInput"
              :hide-stock-disponibility="hideStockDisponibility"
              @selection="selectedItem"
            />
          </div>
        </Transition>
        <div
          v-if="loadingMore && currentPage > 1"
          class="flex justify-center py-2"
        >
          <LoadingComponent :display-label="false" size="small" />
        </div>
      </template>
    </div>
    <div v-else class="mx-auto mt-16 text-center text-slate-300">
      {{
        $t('global.there_is_no_yet', {
          settings: $t(`enums.invoicing.defaultItemCategories.${category}`)
        })
      }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ItemModalCard from '@ui/modal/item/components/ItemModalCard'
import PackModalCard from '@ui/modal/item/components/PackModalCard'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import SelectItemCategory from '@c/InitialStateFormComponent/Models/SelectItemCategory.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import { debounce } from '@u/debounce'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'

export default {
  name: 'ListItemChooser',
  components: {
    SelectComponent,
    BaseSearchInput,
    WalkmanButton,
    LoadingComponent,
    SelectItemCategory,
    FontAwesomeIcon,
    ItemModalCard,
    PackModalCard
  },
  props: {
    selectedCategory: {
      type: String,
      required: false,
      default: null
    },
    canEditCategory: {
      type: Boolean,
      required: false,
      default: false
    },
    displayPackTab: {
      type: Boolean,
      required: false,
      default: true
    },
    funeralHomeId: {
      type: String,
      required: false,
      default: null
    },
    itemTypeAvailable: {
      type: Array,
      required: false,
      default: () => ['item', 'pack', 'dossier']
    },
    calledContext: {
      type: String,
      required: false,
      default: null
    },
    dossierId: {
      type: String,
      required: false,
      default: null
    },
    alreadyAddedItems: {
      type: Array,
      required: false,
      default: () => []
    },
    hideQuantityInput: {
      type: Boolean,
      required: false,
      default: false
    },
    hidePrice: {
      type: Boolean,
      required: false,
      default: false
    },
    hideStockDisponibility: {
      type: Boolean,
      required: false,
      default: false
    },
    disableButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    currency: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: ['close', 'selected-item'],
  data() {
    return {
      localSelectedCategory: this.selectedCategory,
      items: null,
      itemsType: 'item',
      favorite: false,
      best_selling: false,
      filterText: '',
      searchText: '',
      loading: false,
      alreadyAddedItemsDossier: [],
      variants: [],
      children_categories: null,
      suppliers: null,
      selected_children_category: null,
      selected_supplier: null,
      currentPage: 1,
      lastPage: 1,
      perPage: 100,
      loadingMore: false,
      allItemsLoaded: false,
      allLoadedItems: []
    }
  },
  computed: {
    ...mapGetters({
      initialMetadata: 'metadata/getInitialMetadata',
      currencySettings: 'metadata/getDefaultCurrencyMetadata',
      invoice: 'invoicing/getInvoice'
    }),
    defaultCurrency() {
      return this.currency ?? this.currencySettings
    },
    filteredItemTypeAvailable() {
      return [
        { value: 'dossier', label: this.$t('global.dossier') },
        { value: 'item', label: this.$t('attributes.items') },
        { value: 'pack', label: this.$t('attributes.packs') },
        { value: 'automation', label: this.$t('global.automations') }
      ].filter((item) => this.itemTypeAvailable.includes(item.value))
    },
    filteredItem() {
      if (!this.items) {
        return []
      }
      if (this.globalSearch) {
        return this.items.filter(
          (item) =>
            !item.component_parent_id ||
            this.isParentAlreadyAddedToInvoice(item.component_parent_id)
        )
      }

      const _filterText = this.$h.normalizeSearch(this.filterText)

      return this.items
        .filter(
          (item) =>
            !item.component_parent_id ||
            this.isParentAlreadyAddedToInvoice(item.component_parent_id)
        )
        .filter((item) => {
          if (!item.name[this.$i18n.locale]) {
            return item.index_name
          }
          return (
            this.$h
              .normalizeSearch(item.name[this.$i18n.locale])
              .includes(_filterText) ||
            this.$h.normalizeSearch(item.ref)?.includes(_filterText)
          )
        })
        .when(this.selected_children_category, (items) =>
          items.filter((item) => {
            return item.item_category_id === this.selected_children_category
          })
        )
        .when(this.selected_supplier, (items) =>
          items.filter((item) => {
            return item.supplier_id === this.selected_supplier
          })
        )
    },
    componentsByParentId() {
      return this.$h.groupComponentsByParentId(this.items)
    },
    itemsById() {
      const map = {}
      ;(this.items ?? []).forEach((item) => {
        map[item.id] = item
      })
      return map
    },
    categoryName() {
      return this.initialMetadata?.models?.item_categories?.find(
        (item_category) => item_category.id === this.localSelectedCategory
      )?.name
    },
    globalSearch() {
      return this.itemsType === 'item' && !this.localSelectedCategory
    }
  },
  watch: {
    searchText() {
      this.debouncedSearch(this.searchText)
    },
    filterText() {
      this.debouncedSearch(this.filterText)
    },
    itemsType() {
      this.items = null
      this.loadItems()
    },
    favorite() {
      this.filterText = ''
      this.currentPage = 1
      this.allItemsLoaded = false
      this.items = null
      this.allLoadedItems = []
      this.debouncedSearch.cancel()
      this.loadItems()
    },
    localSelectedCategory(newValue, oldValue) {
      this.items = null
      if (newValue !== oldValue && newValue) {
        this.selected_supplier = null
        this.loadItems()
      }
    }
  },
  mounted() {
    if (this.itemTypeAvailable.includes('dossier')) {
      this.itemsType = 'dossier'
    } else if (this.selectedCategory === 'pack') {
      this.itemsType = 'pack'
      this.localSelectedCategory = null
    }
    if (this.localSelectedCategory) {
      this.loadItems()
    }
  },
  created() {
    this.debouncedSearch = debounce((term) => {
      if (this.globalSearch) {
        if (term && term.length >= 3) this.loadItems(false, term)
        else this.items = []
      } else if (this.localSelectedCategory) {
        if (term && term.length >= 3) this.loadItems(false, term)
        else this.items = [...this.allLoadedItems]
      }
    }, 500)

    // if (!this.searchText) {
    this.debouncedHandleScroll = debounce(this.handleScroll, 200)
    // }
  },
  methods: {
    ...mapActions({
      processFetchItemByTermsAction: 'item/fetchItemByTerms',
      processFetchItemByCategoryAction: 'item/fetchItemByCategory',
      processFetchPackForInvoiceAction: 'item/fetchPackForInvoice',
      processFetchItemsDossierForInvoiceAction:
        'item/fetchItemsDossierForInvoice',
      processFetchAutomationsItemsAction: 'invoicing/fetchAutomationsItems'
    }),
    isParentAlreadyAddedToInvoice(parentId) {
      return this.$h.isItemAlreadyInvoiced(this.itemsById[parentId])
    },
    loadItemsDossier(silently) {
      if (this.dossierId) {
        if (!silently) this.loading = true
        this.processFetchItemsDossierForInvoiceAction({
          dossier_id: this.dossierId
        })
          .then((items) => {
            this.items = items
            this.alreadyAddedItemsDossier = this.items.reduce(
              (accumulator, item) => {
                return [
                  ...accumulator,
                  ...item.invoice_items_in_invoice.map(
                    (invoice_item_in_invoice) => invoice_item_in_invoice.item_id
                  )
                ]
              },
              []
            )
          })
          .finally(() => (this.loading = false))
      } else {
        this.$h.toastDanger('dossier id inconnu')
      }
    },
    loadItemsAutomations() {
      this.loading = true
      this.processFetchAutomationsItemsAction({
        invoice_id: this.invoice.id
      })
        .then((response) => {
          this.items = response.items_from_automations?.flatMap(
            (obj) => obj.items || []
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
    async loadItems(loadMore = false, term = '') {
      // --- Packs ---
      if (this.itemsType === 'pack') {
        this.loading = true
        const packs = await this.processFetchPackForInvoiceAction({
          funeral_home_id: this.funeralHomeId
        })
        this.items = this.funeralHomeId
          ? packs.filter((item) =>
              item.funeral_homes.includes(this.funeralHomeId)
            )
          : packs
        this.loading = false
        return
      }

      if (this.itemsType === 'dossier') {
        await this.loadItemsDossier(false)
        return
      }

      if (this.itemsType === 'automation') {
        await this.loadItemsAutomations(false)
        return
      }

      if (this.itemsType === 'item') {
        if (this.globalSearch) {
          if (!term || term.length < 3) {
            this.items = []
            return
          }
          this.loading = true
          try {
            const response = await this.processFetchItemByCategoryAction({
              term,
              funeral_home_id: this.funeralHomeId,
              calledContext: this.calledContext
            })
            this.items = response.items?.data || []
          } finally {
            this.loading = false
          }
          return
        }

        if (!loadMore) {
          if (!term) {
            this.currentPage = 1
            this.allItemsLoaded = false
            this.loading = true
            this.items = []
          } else if (this.currentPage < this.lastPage) {
            this.loading = true
            this.items = []
          }
        } else {
          this.loadingMore = true
        }

        if (!term || this.currentPage < this.lastPage) {
          try {
            const response = await this.processFetchItemByCategoryAction({
              category: this.localSelectedCategory,
              funeral_home_id: this.funeralHomeId,
              term,
              page: this.currentPage,
              per_page: this.perPage,
              favorite: this.favorite || undefined,
              calledContext: this.calledContext
            })
            const newItems = response.items.data || []

            if (loadMore) {
              this.items = [...(this.items || []), ...newItems]
              this.allLoadedItems = [
                ...(this.allLoadedItems || []),
                ...newItems
              ]
            } else {
              this.items = newItems
              if (!term) {
                this.allLoadedItems = newItems
              }
            }

            if (!term) {
              this.currentPage = response.pagination.current_page
              this.lastPage = response.pagination.last_page
              this.allItemsLoaded = this.currentPage >= this.lastPage
            }

            this.children_categories =
              response.metadata?.children_categories ?? null
            this.suppliers = response.metadata?.suppliers ?? []
          } finally {
            this.loading = false
            this.loadingMore = false
          }
        }
      }
    },
    selectedItem(payload) {
      const isPackPayload = payload && payload.pack

      this.$emit('selected-item', {
        type: this.itemsType,
        selectedItem: payload,
        selectPrice: isPackPayload
          ? payload.pack?.selectPrice
          : payload?.selectPrice,
        done: isPackPayload ? payload.done : undefined
      })
      if (this.itemsType === 'dossier') {
        this.loadItemsDossier(true)
      }
    },
    loadMore() {
      if (this.currentPage < this.lastPage) {
        this.currentPage++
        this.loadItems(true, this.filterText)
      }
    },
    handleScroll() {
      // if(!this.filterText) {
      const container = this.$refs.scrollContainer
      if (!container) return

      const bottomReached =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 400

      if (bottomReached && !this.loading && this.currentPage < this.lastPage) {
        this.loadMore()
      }
      // }
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
